import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  ArrowLeft, Calendar, MapPin, Users, Loader2, AlertCircle, CreditCard, QrCode,
  User, Sparkles
} from 'lucide-react';
import MemberLookup, { MemberMatchedBanner } from './MemberLookup';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EventRegistration() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [eventError, setEventError] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const [form, setForm] = useState({
    parent_name: '',
    parent_email: '',
    parent_phone: '',
    child_name: '',
    child_dob: '',
    child_gender: '',
    age_group: '',
    payment_method: 'stripe',
  });

  // Member match state — populated after MemberLookup fires onMatch
  const [matchedParent, setMatchedParent] = useState(null);
  const [matchedChildren, setMatchedChildren] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const res = await fetch(`${API_URL}/api/events/public`);
        if (!res.ok) throw new Error(`Could not load events (${res.status})`);
        const all = await res.json();
        const match = all.find((e) => e.slug === slug);
        if (!match) {
          setEventError('Event not found or no longer available.');
        } else {
          setEvent(match);
        }
      } catch (e) {
        console.error(e);
        setEventError(e.message);
      } finally {
        setLoadingEvent(false);
      }
    };
    fetchEvent();
  }, [slug]);

  const handleMatch = (parent, children) => {
    setMatchedParent(parent);
    setMatchedChildren(children || []);
    setSelectedChildId(null);
    setForm((f) => ({
      ...f,
      parent_name: parent?.name || f.parent_name,
      parent_email: parent?.email || f.parent_email,
      parent_phone: parent?.phone || f.parent_phone,
    }));
    setShowForm(true);
  };

  const handleSelectExistingChild = (child) => {
    setSelectedChildId(child.player_id || child.id);
    setForm((f) => ({
      ...f,
      child_name: child.name || '',
      child_dob: child.birthdate || child.dob || '',
      child_gender: child.gender || '',
    }));
  };

  const handleSkipLookup = () => {
    setShowForm(true);
  };

  const handleChangeMatch = () => {
    // Reset and let parent search again
    setMatchedParent(null);
    setMatchedChildren([]);
    setSelectedChildId(null);
    setShowForm(false);
  };

  const handleChange = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSubmitError(null);
  };

  const validate = () => {
    const required = ['parent_name', 'parent_email', 'parent_phone', 'child_name', 'child_dob', 'child_gender', 'age_group', 'payment_method'];
    for (const k of required) {
      if (!form[k] || String(form[k]).trim() === '') {
        return `Please fill in: ${k.replace(/_/g, ' ')}`;
      }
    }
    if (!/^\S+@\S+\.\S+$/.test(form.parent_email)) return 'Please enter a valid email address.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setSubmitError(err); return; }

    setSubmitting(true);
    setSubmitError(null);

    const origin = window.location.origin;
    const payload = {
      ...form,
      success_url: `${origin}/event-success`,
      cancel_url: `${origin}/event-cancelled`,
    };

    try {
      const res = await fetch(`${API_URL}/api/events/${slug}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || `Registration failed (${res.status})`);
      }

      // Stripe → redirect to checkout
      if (data.payment_method === 'stripe' && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }

      // PayNow → navigate to success page, passing QR + payment details via router state
      // (Portal's GET /registrations/{id} does NOT return paynow_qr_datauri, only POST does)
      if (data.payment_method === 'paynow' && data.registration_id) {
        navigate(`/event-success?registration_id=${data.registration_id}`, {
          state: {
            paynow_qr_datauri: data.paynow_qr_datauri,
            payment_reference: data.payment_reference,
            amount: data.amount,
            currency: data.currency,
            payment_instructions: data.payment_instructions,
          },
        });
        return;
      }

      // Fallback
      if (data.registration_id) {
        navigate(`/event-success?registration_id=${data.registration_id}`);
      } else {
        throw new Error('Unexpected response from Portal.');
      }
    } catch (e) {
      setSubmitError(e.message);
      setSubmitting(false);
    }
  };

  // === Render ===

  if (loadingEvent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  if (eventError || !event) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h2 className="font-display text-2xl text-foreground mb-2">Event Not Available</h2>
        <p className="text-muted-foreground mb-6">{eventError || 'This event could not be loaded.'}</p>
        <Button onClick={() => navigate('/register')} data-testid="event-back-to-hub">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Registration Hub
        </Button>
      </div>
    );
  }

  const formatDateRange = () => {
    const opts = { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' };
    const s = new Date(event.start_date).toLocaleDateString('en-SG', opts);
    if (!event.end_date || event.end_date === event.start_date) return s;
    const e = new Date(event.end_date).toLocaleDateString('en-SG', opts);
    return `${s} – ${e}`;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="event-registration-page">
      {/* Header */}
      <div className="bg-charcoal border-b border-border sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/register')} className="text-muted-foreground hover:text-primary">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="font-display text-xl text-primary">Event Registration</h1>
            <p className="text-xs text-muted-foreground">SCC Football Academy</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Event Summary */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-gradient-to-br from-yellow-900/30 to-amber-900/20 border-yellow-600/50 relative overflow-hidden">
            <div className="absolute top-4 right-4">
              <Sparkles className="h-5 w-5 text-yellow-400 animate-pulse" />
            </div>
            <CardHeader>
              <CardTitle className="font-display text-3xl text-yellow-300">{event.name}</CardTitle>
              {event.description && (
                <CardDescription className="text-yellow-100/70">{event.description}</CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2 text-card-foreground">
                  <Calendar className="h-4 w-4 text-yellow-400" /> {formatDateRange()}
                </div>
                <div className="flex items-center gap-2 text-card-foreground">
                  <MapPin className="h-4 w-4 text-yellow-400" /> {event.location}
                </div>
                <div className="flex items-center gap-2 text-card-foreground">
                  <Users className="h-4 w-4 text-yellow-400" /> {event.age_groups?.join(', ')}
                </div>
                <div className="flex items-center gap-2 text-card-foreground font-bold">
                  <span className="text-yellow-300 text-lg">${event.price} {event.currency}</span>
                  <span className="text-xs text-muted-foreground font-normal">per child</span>
                </div>
              </div>
              {typeof event.spots_remaining === 'number' && event.spots_remaining <= 5 && event.spots_remaining > 0 && (
                <div className="mt-3 text-orange-300 text-sm font-medium">
                  Only {event.spots_remaining} spots left — register soon!
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Member Lookup — appears before the form */}
        {!showForm && (
          <MemberLookup onMatch={handleMatch} onSkip={handleSkipLookup} />
        )}

        {showForm && (
        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
          {/* Matched-member banner */}
          {matchedParent && (
            <MemberMatchedBanner parent={matchedParent} onChange={handleChangeMatch} />
          )}

          {/* Existing children selector — only when matched parent has children */}
          {matchedParent && matchedChildren.length > 0 && (
            <Card className="bg-gradient-card border-green-500/30" data-testid="existing-children-block">
              <CardHeader>
                <CardTitle className="font-display text-lg text-green-300 flex items-center gap-2">
                  <Users className="h-5 w-5" /> Select an existing child
                </CardTitle>
                <CardDescription>
                  Or click "Add new child" below to register someone different.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-3">
                  {matchedChildren.map((c) => {
                    const cid = c.player_id || c.id;
                    const selected = selectedChildId === cid;
                    return (
                      <button
                        key={cid || c.name}
                        type="button"
                        onClick={() => handleSelectExistingChild(c)}
                        className={`text-left p-3 rounded-lg border transition-all ${
                          selected ? 'bg-green-500/15 border-green-400' : 'bg-charcoal border-border hover:border-green-500/50'
                        }`}
                        data-testid={`existing-child-${cid || c.name}`}
                      >
                        <div className="font-medium text-card-foreground">{c.name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {c.birthdate && <span className="mr-2">DOB: {c.birthdate}</span>}
                          {c.membership_number && <span className="font-mono">#{c.membership_number}</span>}
                        </div>
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedChildId(null);
                      setForm((f) => ({ ...f, child_name: '', child_dob: '', child_gender: '' }));
                    }}
                    className={`text-left p-3 rounded-lg border-2 border-dashed transition-all ${
                      selectedChildId === null ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                    }`}
                    data-testid="add-new-child-btn"
                  >
                    <div className="font-medium text-primary">+ Add a new child</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Register someone not in your account</div>
                  </button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Parent details */}
          <Card className="bg-gradient-card gold-border">
            <CardHeader>
              <CardTitle className="font-display text-xl text-primary flex items-center gap-2">
                <User className="h-5 w-5" /> Parent / Guardian
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="parent_name">Full name *</Label>
                  <Input
                    id="parent_name"
                    data-testid="event-parent-name"
                    value={form.parent_name}
                    onChange={(e) => handleChange('parent_name', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="parent_phone">Mobile *</Label>
                  <Input
                    id="parent_phone"
                    data-testid="event-parent-phone"
                    value={form.parent_phone}
                    onChange={(e) => handleChange('parent_phone', e.target.value)}
                    placeholder="+65 9123 4567"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="parent_email">Email *</Label>
                <Input
                  id="parent_email"
                  type="email"
                  data-testid="event-parent-email"
                  value={form.parent_email}
                  onChange={(e) => handleChange('parent_email', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Child details */}
          <Card className="bg-gradient-card gold-border">
            <CardHeader>
              <CardTitle className="font-display text-xl text-primary flex items-center gap-2">
                <Users className="h-5 w-5" /> Child Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="child_name">Child's full name *</Label>
                  <Input
                    id="child_name"
                    data-testid="event-child-name"
                    value={form.child_name}
                    onChange={(e) => handleChange('child_name', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="child_dob">Date of birth *</Label>
                  <Input
                    id="child_dob"
                    type="date"
                    data-testid="event-child-dob"
                    value={form.child_dob}
                    onChange={(e) => handleChange('child_dob', e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Gender *</Label>
                  <Select value={form.child_gender} onValueChange={(v) => handleChange('child_gender', v)}>
                    <SelectTrigger data-testid="event-child-gender">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Age group *</Label>
                  <Select value={form.age_group} onValueChange={(v) => handleChange('age_group', v)}>
                    <SelectTrigger data-testid="event-age-group">
                      <SelectValue placeholder="Select age group" />
                    </SelectTrigger>
                    <SelectContent>
                      {(event.age_groups || []).map((ag) => (
                        <SelectItem key={ag} value={ag}>{ag}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment method */}
          <Card className="bg-gradient-card gold-border">
            <CardHeader>
              <CardTitle className="font-display text-xl text-primary flex items-center gap-2">
                Payment Method
              </CardTitle>
              <CardDescription>
                Choose how you'd like to pay. Both options are processed securely by SCC.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={form.payment_method}
                onValueChange={(v) => handleChange('payment_method', v)}
                className="grid sm:grid-cols-2 gap-3"
              >
                <Label
                  htmlFor="pm-stripe"
                  className={`cursor-pointer p-4 rounded-lg border-2 transition-all ${form.payment_method === 'stripe' ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/40'}`}
                >
                  <RadioGroupItem value="stripe" id="pm-stripe" className="sr-only" data-testid="event-pm-stripe" />
                  <div className="flex items-center gap-3">
                    <CreditCard className="h-5 w-5 text-primary" />
                    <div>
                      <div className="font-medium">Credit / Debit Card</div>
                      <div className="text-xs text-muted-foreground">Visa, Mastercard, AmEx</div>
                    </div>
                  </div>
                </Label>
                <Label
                  htmlFor="pm-paynow"
                  className={`cursor-pointer p-4 rounded-lg border-2 transition-all ${form.payment_method === 'paynow' ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/40'}`}
                >
                  <RadioGroupItem value="paynow" id="pm-paynow" className="sr-only" data-testid="event-pm-paynow" />
                  <div className="flex items-center gap-3">
                    <QrCode className="h-5 w-5 text-primary" />
                    <div>
                      <div className="font-medium">PayNow</div>
                      <div className="text-xs text-muted-foreground">Scan & pay from your bank app</div>
                    </div>
                  </div>
                </Label>
              </RadioGroup>
            </CardContent>
          </Card>

          {submitError && (
            <div className="bg-destructive/10 border border-destructive/40 text-destructive text-sm rounded-lg p-3 flex items-start gap-2" data-testid="event-submit-error">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{submitError}</span>
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-base py-6"
            data-testid="event-submit-btn"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
            ) : (
              <>Continue to Payment — ${event.price} {event.currency}</>
            )}
          </Button>
        </form>
        )}
      </div>
    </div>
  );
}
